import {
  useGetIdentity,
  useGetList,
  useListContext,
  useTranslate,
} from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { SortButton } from "@/components/admin/sort-button";
import { Card, CardContent } from "@/components/ui/card";

import { TopToolbar } from "../layout/TopToolbar";
import { CompanyEmpty } from "./CompanyEmpty";
import { CompanyListFilter } from "./CompanyListFilter";
import { ImageList } from "./GridList";
import type { Project } from "../types";
import { useConfigurationContext } from "../root/ConfigurationContext";

export const CompanyList = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;
  return (
    <List
      title={false}
      perPage={25}
      sort={{ field: "name", order: "ASC" }}
      actions={<CompanyListActions />}
      pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
    >
      <CompanyListLayout />
    </List>
  );
};

const CompanyListLayout = () => {
  const { data, isPending, filterValues } = useListContext();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;
  if (!data?.length && !hasFilters) return <CompanyEmpty />;

  return (
    <div className="w-full flex flex-col lg:flex-row gap-6 lg:gap-8">
      <CompanyListFilter />
      <div className="flex flex-col flex-1 gap-4">
        <UnassignedProjectsSummary />
        <ImageList />
      </div>
    </div>
  );
};

const UnassignedProjectsSummary = () => {
  const { currency } = useConfigurationContext();
  const translate = useTranslate();
  const { data, isPending } = useGetList<Project>("projects", {
    filter: { "company_id@is": null },
    pagination: { page: 1, perPage: 10_000 },
    sort: { field: "id", order: "ASC" },
  });
  if (isPending || !data?.length) return null;
  const revenue = data.reduce(
    (sum, project) => sum + (project.contract_amount ?? project.amount ?? 0),
    0,
  );
  const gross = data.reduce(
    (sum, project) => sum + (project.gross_profit ?? 0),
    0,
  );
  const margins = data
    .map((project) => project.margin)
    .filter((margin): margin is number => margin != null);
  const margin = margins.length
    ? margins.reduce((sum, value) => sum + value, 0) / margins.length
    : 0;
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div>
          <p className="font-medium">Unassigned Projects</p>
          <p className="text-xs text-muted-foreground">
            Add an optional referral or partner Company from the Project record.
          </p>
        </div>
        <div className="flex gap-5 text-sm">
          <span>{data.length} projects</span>
          <span>
            {translate("resources.companies.fields.contract_revenue_total")}{" "}
            {revenue.toLocaleString("en-US", {
              style: "currency",
              currency,
              maximumFractionDigits: 0,
            })}
          </span>
          <span>
            {translate("resources.companies.fields.gross_profit_total")}{" "}
            {gross.toLocaleString("en-US", {
              style: "currency",
              currency,
              maximumFractionDigits: 0,
            })}
          </span>
          <span>
            {translate("resources.companies.fields.margin_average")}{" "}
            {(margin * 100).toFixed(1)}%
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const CompanyListActions = () => {
  const translate = useTranslate();
  return (
    <TopToolbar>
      <SortButton fields={["name", "created_at", "nb_contacts"]} />
      <ExportButton />
      <CreateButton
        label={translate("resources.companies.action.new", {
          _: "New Company",
        })}
      />
    </TopToolbar>
  );
};
